import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../contexts/LocalizationContext';
import orderService from '../services/orderService';
import productService from '../services/productService';
import Loading from '../components/Common/Loading';
import SalesOverTimeChart from '../components/Dashboard/SalesOverTimeChart';
import OrderStatusChart from '../components/Dashboard/OrderStatusChart';
import GrowthChart from '../components/Dashboard/GrowthChart';
import IraqMapChart from '../components/Dashboard/IraqMapChart';
import { format } from 'date-fns';

const getLast30DaysKey = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
};

const buildSalesFromOrders = (orders) => {
  const now = new Date();
  const start = new Date(now);
  start.setDate(start.getDate() - 30);
  start.setHours(0, 0, 0, 0);
  const byDate = {};
  for (let d = new Date(start); d <= now; d.setDate(d.getDate() + 1)) {
    const key = d.toISOString().slice(0, 10);
    byDate[key] = { date: key, revenue: 0, orderCount: 0 };
  }
  (orders || []).forEach((order) => {
    const created = order.createdAtUtc ?? order.createdAt ?? order.date;
    const date = created ? new Date(created) : new Date();
    if (date < start) return;
    if (String(order.status).toLowerCase() !== 'shipped' && order.status !== 2) return;
    const key = getLast30DaysKey(date);
    if (!byDate[key]) byDate[key] = { date: key, revenue: 0, orderCount: 0 };
    byDate[key].revenue += Number(order.total) || 0;
    byDate[key].orderCount += 1;
  });
  return Object.keys(byDate)
    .sort()
    .map((k) => byDate[k]);
};

const useCountUp = (target, isReady) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!isReady) { setValue(0); return; }
    const targetNum = Number(target) || 0;
    if (targetNum === 0) { setValue(0); return; }
    const duration = 1100;
    const startTime = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * targetNum));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, isReady]);
  return value;
};

const Dashboard = () => {
  const { isRtl } = useLocalization();
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [salesOverTime, setSalesOverTime] = useState([]);
  const [governorates, setGovernorates] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const animatedTotalOrders   = useCountUp(statistics?.totalOrders   || 0, !isLoading);
  const animatedPendingOrders = useCountUp(statistics?.pendingOrders || 0, !isLoading);
  const animatedTotalProducts = useCountUp(totalProducts,                   !isLoading);
  const animatedRevenue       = useCountUp(statistics?.totalRevenue  || 0, !isLoading);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [stats, activities, products, byGovernorate, growthData] = await Promise.all([
        orderService.getStatistics().catch(() => null),
        orderService.getRecentActivities(10).catch(() => []),
        productService.getAll().catch(() => []),
        orderService.getByGovernorate().catch(() => []),
        orderService.getGrowth(6).catch(() => []),
      ]);

      setStatistics(stats);
      setGovernorates(Array.isArray(byGovernorate) ? byGovernorate : []);
      setGrowth(Array.isArray(growthData) ? growthData : []);
      setRecentActivities(activities);
      setTotalProducts(products.length || 0);

      const end = new Date();
      const start = new Date(end);
      start.setDate(start.getDate() - 30);
      let salesData = [];
      try {
        const res = await orderService.getSalesOverTime(
          start.toISOString().slice(0, 10),
          end.toISOString().slice(0, 10),
          'day'
        );
        const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        salesData = raw.map((d) => ({
          date: d.date,
          revenue: Number(d.revenue ?? d.totalRevenue ?? 0),
          orderCount: Number(d.orderCount ?? d.ordersCount ?? d.count ?? 0),
        }));
      } catch (_) {
        const allOrders = [];
        for (let page = 1; page <= 5; page++) {
          const result = await orderService.getAll(page, 100).catch(() => ({ items: [] }));
          const items = result?.items || [];
          if (items.length === 0) break;
          allOrders.push(...items);
          if (items.length < 100) break;
        }
        salesData = buildSalesFromOrders(allOrders);
      }
      setSalesOverTime(salesData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (isRtl) return hour < 12 ? 'صباح الخير' : 'مساء الخير';
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getFormattedDate = () => {
    const now = new Date();
    if (isRtl) {
      return now.toLocaleDateString('ar-SA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    }
    return format(now, 'EEEE, MMMM d, yyyy');
  };

  const formatCurrency = (amount) => {
    const suffix = isRtl ? ' د.ع' : ' IQD';
    if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M${suffix}`;
    if (amount >= 1000) return `${(amount / 1000).toFixed(0)}K${suffix}`;
    return `${amount.toFixed(0)}${suffix}`;
  };

  const formatTimeAgo = (timestamp) => {
    const timeSpan = new Date() - new Date(timestamp);
    const minutes = Math.floor(timeSpan / 60000);
    const hours   = Math.floor(timeSpan / 3600000);
    const days    = Math.floor(timeSpan / 86400000);
    if (isRtl) {
      if (minutes < 1)  return 'الآن';
      if (minutes < 60) return `منذ ${minutes} دقيقة`;
      if (hours < 24)   return `منذ ${hours} ساعة`;
      return `منذ ${days} يوم`;
    }
    if (minutes < 1)  return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24)   return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading dashboard...'} />;
  }

  return (
    <div className="dashboard-page">

      {/* Welcome Banner */}
      <div className="welcome-banner animate-in">
        <div className="welcome-banner-content">
          <div className="welcome-badge">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
            {isRtl ? 'رمال الخليج' : 'Rimal Al-Khalij'}
          </div>
          <h1 className="welcome-title">{getGreeting()}</h1>
          <p className="welcome-date">{getFormattedDate()}</p>
        </div>
        <div className="welcome-orbs" aria-hidden="true">
          <div className="welcome-orb welcome-orb-1" />
          <div className="welcome-orb welcome-orb-2" />
          <div className="welcome-orb welcome-orb-3" />
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card animate-in animate-delay-1" onClick={() => navigate('/orders')} style={{ cursor: 'pointer' }}>
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <path d="M16 10a4 4 0 0 1-8 0"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{animatedTotalOrders.toLocaleString()}</div>
              <div className="stat-label">{isRtl ? 'إجمالي الطلبات' : 'Total Orders'}</div>
            </div>
          </div>
        </div>

        <div className="stat-card animate-in animate-delay-2" onClick={() => navigate('/orders?status=pending')} style={{ cursor: 'pointer' }}>
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-warning">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{animatedPendingOrders.toLocaleString()}</div>
              <div className="stat-label">{isRtl ? 'طلبات معلقة' : 'Pending Orders'}</div>
            </div>
          </div>
        </div>

        <div className="stat-card animate-in animate-delay-3" onClick={() => navigate('/products')} style={{ cursor: 'pointer' }}>
          <div className="stat-card-content">
            <div className="stat-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{animatedTotalProducts.toLocaleString()}</div>
              <div className="stat-label">{isRtl ? 'المنتجات' : 'Products'}</div>
            </div>
          </div>
        </div>

        <div className="stat-card animate-in animate-delay-4">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="1" x2="12" y2="23"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{formatCurrency(animatedRevenue)}</div>
              <div className="stat-label">{isRtl ? 'الإيرادات' : 'Revenue'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales & Revenue Chart */}
      <div className="card dashboard-chart-card animate-in animate-delay-5">
        <div className="card-header">
          <h3 className="card-title">{isRtl ? 'المبيعات والإيرادات حسب التاريخ' : 'Sales & Revenue by Date'}</h3>
        </div>
        <div className="card-body">
          <SalesOverTimeChart
            data={salesOverTime}
            isRtl={isRtl}
            formatCurrency={formatCurrency}
            height={300}
          />
        </div>
      </div>

      {/* Growth: App vs Website */}
      <div className="card animate-in animate-delay-5" style={{ marginBottom: 'var(--sp-5)' }}>
        <div className="card-header">
          <h3 className="card-title">{isRtl ? 'نمو الطلبات ربع السنوي' : 'Orders Growth by Quarter'}</h3>
        </div>
        <div className="card-body">
          <GrowthChart data={growth} isRtl={isRtl} height={300} />
        </div>
      </div>

      {/* Orders & Revenue by Governorate */}
      <div className="card animate-in animate-delay-5" style={{ marginBottom: 'var(--sp-5)' }}>
        <div className="card-header">
          <h3 className="card-title">{isRtl ? 'الطلبات والإيرادات حسب المحافظة' : 'Orders & Revenue by Governorate'}</h3>
        </div>
        <div className="card-body">
          {governorates.length === 0 ? (
            <div className="chart-empty" style={{ height: 120 }}>
              <p>{isRtl ? 'لا توجد بيانات' : 'No data'}</p>
            </div>
          ) : (
            <IraqMapChart data={governorates} isRtl={isRtl} formatCurrency={formatCurrency} />
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">

        {/* Order Status Chart */}
        <div className="card animate-in animate-delay-5">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'الطلبات حسب الحالة' : 'Orders by Status'}</h3>
            <button className="btn btn-small btn-secondary" onClick={() => navigate('/orders')}>
              {isRtl ? 'عرض الكل' : 'View All'}
            </button>
          </div>
          <div className="card-body">
            <OrderStatusChart statistics={statistics} isRtl={isRtl} height={260} />
          </div>
        </div>

        {/* Order Status List */}
        <div className="card animate-in animate-delay-6">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'حالة الطلبات' : 'Order Status'}</h3>
            <button className="btn btn-small btn-secondary" onClick={() => navigate('/orders')}>
              {isRtl ? 'عرض الكل' : 'View All'}
            </button>
          </div>
          <div className="card-body">
            <div className="status-list">
              <div className="status-row" onClick={() => navigate('/orders?status=pending')}>
                <div className="status-info">
                  <span className="status-dot warning"></span>
                  <span>{isRtl ? 'معلق' : 'Pending'}</span>
                </div>
                <span className="status-count">{statistics?.pendingOrders || 0}</span>
              </div>
              <div className="status-row" onClick={() => navigate('/orders?status=packed')}>
                <div className="status-info">
                  <span className="status-dot info"></span>
                  <span>{isRtl ? 'تم التعبئة' : 'Packed'}</span>
                </div>
                <span className="status-count">{statistics?.packedOrders || 0}</span>
              </div>
              <div className="status-row" onClick={() => navigate('/orders?status=shipped')}>
                <div className="status-info">
                  <span className="status-dot primary"></span>
                  <span>{isRtl ? 'تم الشحن' : 'Shipped'}</span>
                </div>
                <span className="status-count">{statistics?.shippedOrders || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card animate-in animate-delay-7">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'إجراءات سريعة' : 'Quick Actions'}</h3>
          </div>
          <div className="card-body">
            <div className="quick-actions">
              <button className="action-btn" onClick={() => navigate('/products')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span>{isRtl ? 'إضافة منتج' : 'Add Product'}</span>
              </button>
              <button className="action-btn" onClick={() => navigate('/orders')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                </svg>
                <span>{isRtl ? 'إدارة الطلبات' : 'Manage Orders'}</span>
              </button>
              <button className="action-btn" onClick={() => navigate('/categories')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                </svg>
                <span>{isRtl ? 'التصنيفات' : 'Categories'}</span>
              </button>
              <button className="action-btn" onClick={() => navigate('/brands')}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                  <line x1="7" y1="7" x2="7.01" y2="7"></line>
                </svg>
                <span>{isRtl ? 'العلامات التجارية' : 'Brands'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="card card-full-width animate-in animate-delay-8">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'النشاط الأخير' : 'Recent Activity'}</h3>
          </div>
          <div className="card-body p-0">
            {recentActivities && recentActivities.length > 0 ? (
              <div className="activity-list">
                {recentActivities.slice(0, 5).map((activity, index) => (
                  <div key={index} className="activity-item">
                    <div className={`activity-icon ${activity.type === 'order' ? 'order' : ''}`}>
                      {activity.type === 'order' ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      )}
                    </div>
                    <div className="activity-content">
                      <p className="activity-text" dangerouslySetInnerHTML={{ __html: activity.description }}></p>
                      <span className="activity-time">{formatTimeAgo(activity.timestamp)}</span>
                    </div>
                    <span className="badge badge-neutral">{activity.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ margin: 'var(--sp-5)' }}>
                <svg className="empty-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <p className="empty-title">{isRtl ? 'لا توجد نشاطات حديثة' : 'No recent activity'}</p>
                <p className="empty-description">{isRtl ? 'ستظهر النشاطات هنا عندما تبدأ' : 'Activities will appear here when they happen'}</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
